// ═══════════════════════════════════════════════════════════════════════════════
//  src/advanced/advancedBank.js
//  ALTO RENDIMIENTO — bancos de los cuatro formatos deterministas
//
//  ── QUÉ SEPARA A UN B2 DE UN C1 ─────────────────────────────────────────────
//  No es gramática. Un B2 ya sabe present perfect. Lo que se le rompe es el
//  MATIZ: suena grosero sin querer, no capta el sarcasmo, dice "big problem"
//  donde un nativo dice "pressing issue", y pierde el 30% de un audio real
//  porque el nativo no habla como la voz de una app.
//
//  El examen "Mide tu condición" ya diagnostica exactamente eso —`hedging`,
//  `diplomatic_language`, `passive_aggressive`, `subtext`, `formal_register`,
//  `idiomatic_advanced`, `vocabulary_in_context`, `inference_audio`— y hasta hoy
//  no había ni un ejercicio que lo entrenara. Este archivo cierra ese hueco.
//
//  ── POR QUÉ NO HAY IA AQUÍ ──────────────────────────────────────────────────
//  Los cuatro formatos de este archivo tienen respuesta correcta única, así que
//  se califican comparando. Pedirle a un modelo que juzgue lo que una tabla ya
//  sabe sería pagar por una opinión donde hay un hecho. La IA se reserva para
//  los dos formatos donde no hay clave: el debate y el correo.
//
//  Cada reactivo lleva `tag` con la competencia del examen, para que el plan de
//  entrenamiento pueda prescribirlo cuando esa competencia salga rota.
// ═══════════════════════════════════════════════════════════════════════════════

/* ═══════════════════════════════════════════════════════════════════════════
   1. TONE SURGERY — la misma idea, tres temperaturas
   ---------------------------------------------------------------------------
   Se da una SITUACIÓN y tres formas de decir lo mismo. Solo una encaja.

   La trampa no es que las otras dos sean incorrectas: son perfectamente
   gramaticales. Están mal por REGISTRO, que es justo lo que no se enseña en
   ningún lado y lo que hace que a un hispanohablante competente lo perciban
   como brusco en una junta en inglés.
   ═══════════════════════════════════════════════════════════════════════════ */

export const TONE = [
  {
    id: 'to-01', tag: 'diplomatic_language', level: 'B2',
    scene: 'En junta con el cliente. Su equipo propone una solución que sabes que va a fallar, y ya están asintiendo.',
    options: [
      { text: "That won't work.", ok: false, why: 'Correcto y demoledor. Cierra la puerta y deja a su equipo en ridículo delante de su jefe.' },
      { text: "I'd want to stress-test that before we commit — can we walk through the failure cases?", ok: true, why: 'Frena la decisión sin acusar a nadie. Invita a revisar juntos en vez de sentenciar.' },
      { text: "Are you sure about that?", ok: false, why: 'Parece suave y no lo es: en inglés suena condescendiente, como si dudaras de su criterio.' },
    ],
  },
  {
    id: 'to-02', tag: 'register', level: 'B2',
    scene: 'Tu jefe te pide algo para mañana. Ya tienes dos entregas ese día y no da el tiempo.',
    options: [
      { text: "I can't do that.", ok: false, why: 'Cierra sin abrir nada. En inglés corporativo un "I can\'t" seco se lee como negativa a colaborar.' },
      { text: "That's going to be tight — I have two deliverables that day. Which one should move?", ok: true, why: 'No dices que no: devuelves la decisión con la información que él no tenía.' },
      { text: "I will try my best.", ok: false, why: 'Suena colaborativo y es la peor opción: no dijiste que no llegas, así que mañana el problema es tuyo.' },
    ],
  },
  {
    id: 'to-03', tag: 'hedging', level: 'B2',
    scene: 'Te preguntan por una fecha de entrega y honestamente no la sabes todavía.',
    options: [
      { text: "I don't know.", ok: false, why: 'Verdadero, y te deja fuera de la conversación. Nadie vuelve a preguntarte.' },
      { text: "Maybe next week, maybe the other one.", ok: false, why: 'Vaguedad sin compromiso. Es la respuesta que hace que dejen de confiar en tus fechas.' },
      { text: "I'd rather not commit to a date until I've checked with the team — I can confirm by Thursday.", ok: true, why: 'Admites que no sabes y aun así entregas algo concreto: una fecha para saberlo.' },
    ],
  },
  {
    id: 'to-04', tag: 'diplomatic_language', level: 'C1',
    scene: 'Un compañero entregó código con errores que te costó dos horas arreglar. Se lo tienes que decir.',
    options: [
      { text: "You broke the build again.", ok: false, why: '"You" + pasado + "again" es la fórmula exacta de una acusación. La conversación ya terminó mal.' },
      { text: "The build was failing this morning — I patched the null check in the parser, worth a look before the next push.", ok: true, why: 'Habla del código, no de la persona. Deja el aprendizaje sin dejar el reproche.' },
      { text: "No worries, I fixed it.", ok: false, why: 'Demasiado suave: no se entera de nada y lo vuelve a hacer la semana que viene.' },
    ],
  },
  {
    id: 'to-05', tag: 'register', level: 'B2',
    scene: 'Escribes a un cliente que lleva tres semanas sin pagar la factura.',
    options: [
      { text: "You still haven't paid invoice 402.", ok: false, why: 'Los hechos son correctos y el tono es de cobrador. Con un cliente que quieres conservar, es caro.' },
      { text: "Just checking in on invoice 402 — could you let me know where it stands?", ok: true, why: 'Firme y sin fricción. "Where it stands" pide una respuesta concreta sin acusar.' },
      { text: "Sorry to bother you, I know you are very busy, but maybe if possible…", ok: false, why: 'Tanta disculpa te quita la autoridad para cobrar. Pedir lo que te deben no requiere perdón.' },
    ],
  },
  {
    id: 'to-06', tag: 'diplomatic_language', level: 'C1',
    scene: 'En una revisión de diseño quieres decir que la propuesta no resuelve el problema del usuario.',
    options: [
      { text: "This misses the point.", ok: false, why: 'Directo al punto y directo al conflicto. Nadie defiende bien su trabajo después de oír eso.' },
      { text: "I'm not sure this addresses the original problem — could you walk me through the user flow again?", ok: true, why: 'Pone la duda en ti, no en su trabajo, y le da la oportunidad de verlo solo.' },
      { text: "It's nice, but I don't like it.", ok: false, why: '"I don\'t like it" es gusto personal. En una revisión profesional, el gusto no es un argumento.' },
    ],
  },
  {
    id: 'to-07', tag: 'formal_register', level: 'C1',
    scene: 'Rechazas por escrito una oferta de trabajo que sí te interesó, y quieres dejar la puerta abierta.',
    options: [
      { text: "Thanks but no thanks.", ok: false, why: 'Es una frase hecha con carga sarcástica. Cierra la puerta y además suena burlona.' },
      { text: "After a lot of thought I've decided to stay where I am — I hope we can keep in touch.", ok: true, why: 'Dice que no sin excusas inventadas y deja la relación viva de forma creíble.' },
      { text: "I am very sorry, I cannot accept, please do not be angry with me.", ok: false, why: 'Pedir perdón por una decisión profesional te posiciona como alguien que se puede presionar.' },
    ],
  },
  {
    id: 'to-08', tag: 'register', level: 'B2',
    scene: 'Interrumpes una reunión porque tienes un dato que cambia la decisión que están tomando.',
    options: [
      { text: "Sorry, sorry — can I say something?", ok: false, why: 'Dos disculpas antes de hablar y nadie sabe todavía por qué interrumpes. Te van a dejar para el final.' },
      { text: "Quick flag before we decide — the numbers changed on Friday.", ok: true, why: 'Anuncia el valor antes de pedir el turno. Por eso te dan el turno.' },
      { text: "Excuse me, I have something very important to say right now.", ok: false, why: 'Anunciar que lo tuyo es importante sin decir qué es, obliga a los demás a confiar a ciegas.' },
    ],
  },
  {
    id: 'to-09', tag: 'diplomatic_language', level: 'C1',
    scene: 'Te piden que asumas el trabajo de alguien que se fue, sin ajuste de sueldo ni de plazos.',
    options: [
      { text: "That's not my job.", ok: false, why: 'Cierto y radioactivo. Es la frase que la gente recuerda en la siguiente evaluación.' },
      { text: "Happy to help. Let's talk about what comes off my plate to make room for it.", ok: true, why: 'Aceptas la conversación, no la carga. Y pones el costo sobre la mesa sin negarte.' },
      { text: "Okay, no problem.", ok: false, why: 'El precio de esta respuesta lo pagas en tres semanas, cuando no llegues a nada.' },
    ],
  },
  {
    id: 'to-10', tag: 'hedging', level: 'B2',
    scene: 'Presentas datos preliminares y alguien pregunta si eso demuestra tu hipótesis.',
    options: [
      { text: "Yes, it proves it.", ok: false, why: '"Prove" es una palabra grande. Si el dato es preliminar, la afirmación te va a perseguir.' },
      { text: "The data points that way, but the sample is still small — I'd call it a strong signal, not proof.", ok: true, why: 'Defiendes tu hallazgo y marcas su límite tú mismo, antes de que lo haga otro.' },
      { text: "More or less, I think, maybe.", ok: false, why: 'Tres matices seguidos borran el hallazgo. Matizar no es dudar de todo.' },
    ],
  },
  {
    id: 'to-11', tag: 'formal_register', level: 'C1',
    scene: 'Un proveedor entregó tarde por segunda vez. Le escribes al responsable de cuenta.',
    options: [
      { text: "This is unacceptable and unprofessional.", ok: false, why: 'Adjetivos de juicio. Sube la temperatura y baja la probabilidad de que te resuelvan.' },
      { text: "This is the second delay this quarter. I need to understand what changes before the next order.", ok: true, why: 'Hecho, patrón y petición concreta. Es más duro que el insulto y mucho más difícil de esquivar.' },
      { text: "We are a bit disappointed, but it is okay.", ok: false, why: '"It is okay" borra la queja. Acabas de autorizar el tercer retraso.' },
    ],
  },
  {
    id: 'to-12', tag: 'diplomatic_language', level: 'C1',
    scene: 'Tu jefe te da crédito en público por un trabajo que hizo en gran parte otra persona del equipo.',
    options: [
      { text: "Actually, it wasn't me.", ok: false, why: 'Correcto de fondo y torpe de forma: contradice a tu jefe en público sin nombrar a quien merece el crédito.' },
      { text: "Thank you — though most of that was Ana's work on the pipeline, she should take this one.", ok: true, why: 'Acepta el gesto y redirige el crédito con nombre y detalle. Es lo que hace un líder.' },
      { text: "Thanks!", ok: false, why: 'Barato y caro a la vez: Ana estaba en la sala.' },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   2. SUBTEXTO — dice A, quiere decir B
   ---------------------------------------------------------------------------
   El inglés profesional está lleno de frases cuyo significado literal no tiene
   nada que ver con su intención. "That's an interesting approach" casi nunca
   significa que le pareció interesante.

   Un B2 entiende cada palabra y aun así se pierde la conversación entera. Esto
   es lo que ningún curso de gramática enseña y lo que más caro se paga.
   ═══════════════════════════════════════════════════════════════════════════ */

export const SUBTEXT = [
  {
    id: 'sb-01', tag: 'subtext', level: 'B2',
    scene: 'Presentas tu propuesta. El director escucha y dice, despacio:',
    line: "That's an interesting approach.",
    voice: { rate: 0.92, pitch: 0.95 },
    options: [
      { text: 'Le gustó y quiere saber más.', ok: false },
      { text: 'No le convence, y te lo está diciendo sin decirlo.', ok: true },
      { text: 'Está pensando en otra cosa.', ok: false },
    ],
    why: 'En inglés corporativo "interesting" es el envoltorio educado de "no". Si le hubiera gustado diría "I like it" o "let\'s do it". Cuando lo oigas, pregunta qué le preocupa — todavía estás a tiempo.',
  },
  {
    id: 'sb-02', tag: 'passive_aggressive', level: 'B2',
    scene: 'Entregas el reporte dos días tarde. Tu compañera responde por chat:',
    line: "No worries at all! I already did it myself. 🙂",
    options: [
      { text: 'De verdad no hay problema, fue amable.', ok: false },
      { text: 'Está molesta y te está marcando que te cubrió.', ok: true },
      { text: 'Te está ofreciendo ayuda para la próxima.', ok: false },
    ],
    why: 'El "at all", el "already" y el "myself" en la misma frase no son casualidad: cada uno subraya que ella hizo lo tuyo. La carita no suaviza, sella. Lo correcto es reconocerlo explícitamente, no responder con otro emoji.',
  },
  {
    id: 'sb-03', tag: 'subtext', level: 'B2',
    scene: 'Pides retroalimentación sobre tu diseño. Te contestan:',
    line: "It's a bold choice.",
    options: [
      { text: 'Admira que te hayas arriesgado.', ok: false },
      { text: 'Le parece un error y está siendo educado.', ok: true },
      { text: 'Es neutral, no opina.', ok: false },
    ],
    why: '"Bold" aplicado a un trabajo ajeno casi siempre significa "yo no lo habría hecho". El elogio real es específico ("the contrast works"); el genérico y abstracto suele ser una salida diplomática.',
  },
  {
    id: 'sb-04', tag: 'tone_indifference', level: 'B2',
    scene: 'Propones adelantar la entrega. Tu líder técnico responde:',
    line: "Sure. Whatever works.",
    voice: { rate: 0.95, pitch: 0.9 },
    options: [
      { text: 'Está de acuerdo y flexible.', ok: false },
      { text: 'Se desentiende: no está de acuerdo pero no va a discutir.', ok: true },
      { text: 'Necesita más información para decidir.', ok: false },
    ],
    why: '"Whatever works" es una retirada, no un acuerdo. Alguien comprometido dice "yes, let\'s do it" o pregunta cómo. Si lo oyes, la decisión no tiene su apoyo real y lo vas a notar en la ejecución.',
  },
  {
    id: 'sb-05', tag: 'subtext', level: 'C1',
    scene: 'Terminas una entrevista de trabajo. El reclutador se levanta y dice:',
    line: "We'll be in touch.",
    options: [
      { text: 'Te van a llamar pronto.', ok: false },
      { text: 'Sin más detalle, es la fórmula de despedida por defecto — no dice nada.', ok: true },
      { text: 'Quedaste descartado.', ok: false },
    ],
    why: 'Es una frase vacía por diseño: no promete ni descarta. La señal está en lo que la acompaña. "We\'ll be in touch by Wednesday" es información; "we\'ll be in touch" a secas es cortesía. Ahí conviene preguntar cuáles son los siguientes pasos.',
  },
  {
    id: 'sb-06', tag: 'passive_aggressive', level: 'C1',
    scene: 'Discrepas con un compañero en un correo con copia a todo el equipo. Él responde:',
    line: "Per my last email, the deadline was always Friday.",
    options: [
      { text: 'Te recuerda un dato amablemente.', ok: false },
      { text: 'Te está señalando en público que no leíste.', ok: true },
      { text: 'Se disculpa por no haber sido claro.', ok: false },
    ],
    why: '"Per my last email" es el clásico del correo corporativo: significa "ya te lo dije y hay registro". Con copia a todos, es una corrección pública. Responder por privado desactiva la escalada; responder a todos la alimenta.',
  },
  {
    id: 'sb-07', tag: 'subtext', level: 'C1',
    scene: 'Pides un aumento. Tu jefe asiente y contesta:',
    line: "Let's revisit this next quarter.",
    options: [
      { text: 'Va a revisarlo el próximo trimestre.', ok: false },
      { text: 'Es un no sin fecha real: sin compromiso concreto, no va a pasar.', ok: true },
      { text: 'Necesita autorización de recursos humanos.', ok: false },
    ],
    why: '"Let\'s revisit" pospone sin comprometer. La respuesta útil es convertirlo en algo verificable: "what would need to be true by then for this to be a yes?". Si no hay respuesta a eso, tienes tu respuesta.',
  },
  {
    id: 'sb-08', tag: 'tone_sarcasm', level: 'B2',
    scene: 'El servidor se cae por tercera vez en la semana. Tu compañero escribe en el canal:',
    line: "Well, this is going great.",
    voice: { rate: 0.88, pitch: 0.85 },
    options: [
      { text: 'Está contento con el progreso.', ok: false },
      { text: 'Es sarcasmo: está harto.', ok: true },
      { text: 'Está siendo optimista para animar al equipo.', ok: false },
    ],
    why: 'El sarcasmo en inglés se marca con el contraste entre lo dicho y la situación, y en voz con entonación plana y alargada. "Well" al inicio suele avisar de que viene lo contrario de lo que se dice.',
  },
  {
    id: 'sb-09', tag: 'subtext', level: 'C1',
    scene: 'Envías tu propuesta de presupuesto. El cliente responde:',
    line: "We're evaluating a few options at the moment.",
    options: [
      { text: 'Tu propuesta va bien encaminada.', ok: false },
      { text: 'Te avisa de que hay competencia y probablemente de precio.', ok: true },
      { text: 'Va a rechazar tu propuesta.', ok: false },
    ],
    why: 'Mencionar que hay otras opciones es una jugada de negociación, no un informe. La lectura útil no es "voy a perder": es "hay margen para diferenciarme o ajustar", y conviene preguntar contra qué te están comparando.',
  },
  {
    id: 'sb-10', tag: 'passive_aggressive', level: 'C1',
    scene: 'Propones un cambio de proceso. Alguien del equipo contesta en la junta:',
    line: "That's one way to do it.",
    options: [
      { text: 'Acepta tu propuesta como válida.', ok: false },
      { text: 'Discrepa y evita decirlo abiertamente.', ok: true },
      { text: 'Ofrece alternativas y quiere explorarlas contigo.', ok: false },
    ],
    why: '"That\'s one way" implica que hay otra mejor que no está diciendo — y que no la va a ofrecer sola. No es una invitación a explorar: es una objeción envuelta. La jugada correcta es sacarla tú: "what would you do instead?". Casi siempre la tiene pensada.',
  },
  {
    id: 'sb-11', tag: 'subtext', level: 'C2',
    scene: 'Después de tu presentación, un directivo comenta:',
    line: "I appreciate the effort that went into this.",
    options: [
      { text: 'Valora el resultado del trabajo.', ok: false },
      { text: 'Elogia el esfuerzo justo porque no puede elogiar el resultado.', ok: true },
      { text: 'Está agradeciendo formalmente antes de aprobar.', ok: false },
    ],
    why: 'Cuando el elogio se dirige al esfuerzo y no al resultado, casi siempre es porque el resultado no da para elogio. Es el equivalente adulto de "te esforzaste mucho". Conviene preguntar directamente qué cambiarían.',
  },
  {
    id: 'sb-12', tag: 'subtext', level: 'C2',
    scene: 'Pides opinión sobre un compañero al que estás considerando para tu equipo. Su exjefe dice:',
    line: "He was always very punctual.",
    options: [
      { text: 'Le está dando una buena referencia.', ok: false },
      { text: 'Solo puede elogiar algo trivial: es una advertencia.', ok: true },
      { text: 'Considera la puntualidad la cualidad más importante.', ok: false },
    ],
    why: 'En referencias laborales lo que NO se dice pesa más que lo que se dice. Elogiar la puntualidad y nada del trabajo es la forma legalmente segura de no recomendar a alguien. Pregunta explícitamente: "would you hire him again?".',
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   3. PRECISIÓN LÉXICA — de "big problem" a "pressing issue"
   ---------------------------------------------------------------------------
   El salto de B2 a C1 no es saber más palabras: es elegir la que va con esa
   otra. "Make a decision" y "do a decision" tienen las mismas palabras
   conocidas y solo una existe.

   Todas las opciones equivocadas son gramaticalmente válidas y semánticamente
   comprensibles. Suenan mal, y sonar mal es exactamente lo que separa a alguien
   que habla inglés de alguien que suena profesional en inglés.
   ═══════════════════════════════════════════════════════════════════════════ */

export const LEXIS = [
  {
    id: 'lx-01', tag: 'vocabulary_in_context', level: 'B2',
    scene: 'Reportando un riesgo al comité.',
    base: 'We have a ___ that needs attention before launch.',
    options: [
      { text: 'big problem', ok: false, why: 'Se entiende y suena de conversación, no de comité.' },
      { text: 'pressing issue', ok: true, why: 'Colocación estándar de registro profesional: urgente sin ser alarmista.' },
      { text: 'strong problem', ok: false, why: '"Strong" no colocan con "problem". Suena a traducción directa.' },
    ],
  },
  {
    id: 'lx-02', tag: 'vocabulary_in_context', level: 'B2',
    scene: 'Hablando de una decisión difícil que tomaste.',
    base: 'I had to ___ a difficult decision under time pressure.',
    options: [
      { text: 'take', ok: false, why: 'Calco de "tomar una decisión". En inglés no se toma, se hace.' },
      { text: 'make', ok: true, why: '"Make a decision" es fijo. Ni take, ni do, ni have.' },
      { text: 'do', ok: false, why: '"Do" va con tareas y trabajo, no con decisiones.' },
    ],
  },
  {
    id: 'lx-03', tag: 'vocabulary_in_context', level: 'B2',
    scene: 'Explicando por qué cambió el alcance del proyecto.',
    base: 'The requirements ___ significantly halfway through.',
    options: [
      { text: 'changed a lot', ok: false, why: 'Correcto y plano. En un reporte escrito se lee informal.' },
      { text: 'shifted', ok: true, why: '"Shift" implica movimiento de dirección, no solo cantidad. Es la palabra de gestión de proyectos.' },
      { text: 'moved themselves', ok: false, why: 'El reflexivo sobra: "move" ya funciona solo.' },
    ],
  },
  {
    id: 'lx-04', tag: 'idiomatic_advanced', level: 'C1',
    scene: 'Reconociendo que un plan tiene un punto débil.',
    base: "There's one ___ in the plan we should address.",
    options: [
      { text: 'bad part', ok: false, why: 'Infantil para un contexto profesional.' },
      { text: 'gap', ok: true, why: '"Gap" nombra el hueco sin condenar el plan entero. Es la palabra que permite seguir la conversación.' },
      { text: 'hole', ok: false, why: 'Existe pero es más agresivo: "a hole in your plan" suena a demolición.' },
    ],
  },
  {
    id: 'lx-05', tag: 'vocabulary_in_context', level: 'B2',
    scene: 'Cerrando un correo donde pides algo con urgencia.',
    base: 'I would ___ your help on this before Friday.',
    options: [
      { text: 'thank', ok: false, why: '"I would thank" no es inglés natural para pedir algo.' },
      { text: 'appreciate', ok: true, why: '"I would appreciate" es la fórmula estándar: educada y con presión implícita.' },
      { text: 'be grateful of', ok: false, why: 'Es "grateful FOR". Y aun corregido, suena excesivamente sumiso.' },
    ],
  },
  {
    id: 'lx-06', tag: 'idiomatic_advanced', level: 'C1',
    scene: 'Diciendo que dos áreas tienen que trabajar juntas.',
    base: 'Engineering and design need to be ___ before we start.',
    options: [
      { text: 'in the same page', ok: false, why: 'Casi: es "ON the same page". La preposición equivocada delata al no nativo al instante.' },
      { text: 'aligned', ok: true, why: 'La palabra de oficina por excelencia para acuerdo previo entre equipos.' },
      { text: 'equal', ok: false, why: '"Equal" es igualdad, no coordinación. Cambia el significado.' },
    ],
  },
  {
    id: 'lx-07', tag: 'vocabulary_in_context', level: 'B2',
    scene: 'Explicando que una tarea llevará más tiempo del previsto.',
    base: 'This is going to ___ more time than we estimated.',
    options: [
      { text: 'take', ok: true, why: '"Take time" es la colocación fija. Simple y correcta.' },
      { text: 'need', ok: false, why: 'Gramatical, pero desplaza el foco: "need" suena a petición, no a estimación.' },
      { text: 'spend', ok: false, why: '"Spend" lo hace una persona, no la tarea: "we will spend more time".' },
    ],
  },
  {
    id: 'lx-08', tag: 'idiomatic_advanced', level: 'C1',
    scene: 'Advirtiendo que una decisión tendrá consecuencias más adelante.',
    base: 'Cutting testing now will ___ later.',
    options: [
      { text: 'be a problem', ok: false, why: 'Correcto y sin fuerza. No transmite que el costo será mayor por haber esperado.' },
      { text: 'come back to bite us', ok: true, why: 'Idiomático, común en reuniones técnicas y transmite exactamente la idea de costo diferido.' },
      { text: 'return against us', ok: false, why: 'Traducción literal que no existe en inglés.' },
    ],
  },
  {
    id: 'lx-09', tag: 'vocabulary_in_context', level: 'B2',
    scene: 'Describiendo tu papel en un proyecto grande.',
    base: 'I was ___ the migration from start to finish.',
    options: [
      { text: 'in charge for', ok: false, why: 'Preposición incorrecta: es "in charge OF".' },
      { text: 'responsible of', ok: false, why: 'Mismo error al revés: es "responsible FOR".' },
      { text: 'responsible for', ok: true, why: 'La forma correcta, y la que se espera en una entrevista.' },
    ],
  },
  {
    id: 'lx-10', tag: 'idiomatic_advanced', level: 'C1',
    scene: 'Pidiendo que alguien te ponga al día tras volver de vacaciones.',
    base: 'Could you ___ on what happened while I was out?',
    options: [
      { text: 'update me', ok: true, why: 'Directo, neutro y universalmente entendido.' },
      { text: 'put me in context', ok: false, why: 'Calco de "ponerme en contexto". No se dice.' },
      { text: 'inform me', ok: false, why: 'Gramatical pero rígido: suena a comunicado oficial entre colegas.' },
    ],
  },
  {
    id: 'lx-11', tag: 'vocabulary_in_context', level: 'B2',
    scene: 'Reconociendo un error propio en una retrospectiva.',
    base: 'I ___ the complexity of the integration.',
    options: [
      { text: 'underestimated', ok: true, why: 'La palabra exacta, y la que un equipo espera oír en una retro.' },
      { text: 'calculated bad', ok: false, why: 'Adverbio mal formado y registro coloquial.' },
      { text: 'did not think well', ok: false, why: 'Se entiende, pero suena a excusa de niño más que a análisis.' },
    ],
  },
  {
    id: 'lx-12', tag: 'idiomatic_advanced', level: 'C1',
    scene: 'Diciendo que una propuesta necesita más trabajo antes de presentarla.',
    base: "It's promising, but it still needs ___.",
    options: [
      { text: 'more work', ok: false, why: 'Correcto y vago. No dice cuánto ni de qué tipo.' },
      { text: 'polishing', ok: true, why: 'Implica que el fondo está bien y falta acabado. Elogia mientras corrige.' },
      { text: 'to be repaired', ok: false, why: '"Repair" implica que está roto, que es lo contrario de lo que quieres decir.' },
    ],
  },
  {
    id: 'lx-13', tag: 'formal_register', level: 'C1',
    scene: 'Cerrando un correo donde entregas malas noticias a un cliente.',
    base: '___ the delay, and thank you for your patience.',
    options: [
      { text: 'Sorry for', ok: false, why: 'Correcto en conversación; en un correo formal a cliente se lee ligero.' },
      { text: 'I apologise for', ok: true, why: 'Asume responsabilidad en primera persona. Es lo que espera un cliente molesto.' },
      { text: 'Excuse me for', ok: false, why: '"Excuse me" es para interrumpir o pasar, no para disculparse por un error.' },
    ],
  },
  {
    id: 'lx-14', tag: 'vocabulary_in_context', level: 'B2',
    scene: 'Explicando una mejora que lograste en producción.',
    base: 'We ___ response time by forty percent.',
    options: [
      { text: 'reduced', ok: true, why: 'La colocación estándar con métricas. Neutra y precisa.' },
      { text: 'lowered down', ok: false, why: '"Lower" ya incluye la dirección: "down" sobra.' },
      { text: 'diminished', ok: false, why: 'Existe, pero es literario. En un contexto técnico suena fuera de lugar.' },
    ],
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   4. OÍDO NATIVO — velocidad real y acentos
   ---------------------------------------------------------------------------
   Un B2 entiende el inglés de las apps porque las apps hablan despacio, sin
   reducciones y con un solo acento. La vida real es lo contrario.

   ── LO QUE ESTE FORMATO PUEDE Y NO PUEDE HACER ─────────────────────────────
   Se usan las voces del navegador, que sí varían por región (en-GB, en-AU,
   en-IN, en-US) y sí se pueden acelerar a velocidad de conversación real. NO
   es audio de hablante nativo grabado: es síntesis con acento regional, más
   rápida y con contracciones escritas para forzar la reducción.

   Es un peldaño intermedio honesto, y la pantalla lo dice tal cual. El salto a
   audio humano real necesita archivos de audio o el pipeline de Deepgram, y esa
   es una decisión aparte con costo aparte.
   ═══════════════════════════════════════════════════════════════════════════ */

export const EAR = [
  {
    id: 'er-01', tag: 'detail_audio', level: 'B2', accent: 'en-GB', rate: 1.25,
    line: "I reckon we'll have to push it back a fortnight, if that's alright with you.",
    q: '¿Cuánto se retrasa?',
    options: [
      { text: 'Dos semanas', ok: true },
      { text: 'Cuatro días', ok: false },
      { text: 'Un mes', ok: false },
    ],
    why: '"Fortnight" son dos semanas y es de uso diario en el inglés británico — prácticamente ausente en el americano. "Reckon" ahí significa "creo", no "calculo".',
  },
  {
    id: 'er-02', tag: 'gist', level: 'B2', accent: 'en-US', rate: 1.3,
    line: "Look, I'm not gonna sugarcoat it — the numbers just aren't there yet.",
    q: '¿Qué te está diciendo?',
    options: [
      { text: 'Los resultados no son suficientes y va a ser directo', ok: true },
      { text: 'Los números todavía no llegan pero van a llegar', ok: false },
      { text: 'Quiere endulzar la noticia', ok: false },
    ],
    why: '"Sugarcoat" es suavizar una mala noticia, y "I\'m not gonna sugarcoat it" avisa de que viene un golpe directo. "Aren\'t there yet" es un no cortés al resultado actual.',
  },
  {
    id: 'er-03', tag: 'inference_audio', level: 'B2', accent: 'en-AU', rate: 1.25,
    line: "Yeah, no, look, it's not a huge deal, but we probably shouldn't do it again.",
    q: '¿Qué tan grave es?',
    options: [
      { text: 'Nada grave, lo dijo claro', ok: false },
      { text: 'Sí importa: el "but" es la parte real del mensaje', ok: true },
      { text: 'Está furioso', ok: false },
    ],
    why: 'El "yeah, no" australiano es un amortiguador, no una contradicción. Todo lo que va antes del "but" es cortesía; lo que va después es el mensaje.',
  },
  {
    id: 'er-04', tag: 'detail_audio', level: 'C1', accent: 'en-IN', rate: 1.2,
    line: "Kindly do the needful and revert by end of day.",
    q: '¿Qué te están pidiendo?',
    options: [
      { text: 'Que hagas lo necesario y respondas hoy', ok: true },
      { text: 'Que deshagas el cambio', ok: false },
      { text: 'Que esperes hasta mañana', ok: false },
    ],
    why: '"Do the needful" y "revert" (por "reply") son fórmulas del inglés de negocios indio, muy comunes en equipos distribuidos. "Revert" ahí NO significa deshacer.',
  },
  {
    id: 'er-05', tag: 'gist', level: 'B2', accent: 'en-GB', rate: 1.3,
    line: "To be fair, it's not the worst idea I've heard this week.",
    q: '¿Le gusta la idea?',
    options: [
      { text: 'Le encanta', ok: false },
      { text: 'Es un elogio muy tibio: no está convencido', ok: true },
      { text: 'La odia', ok: false },
    ],
    why: 'El understatement británico invierte la escala: "not the worst" es tibio, no positivo. Un entusiasmo real sonaría "that\'s brilliant".',
  },
  {
    id: 'er-06', tag: 'inference_audio', level: 'C1', accent: 'en-US', rate: 1.35,
    line: "I hear you, and I'd love to help, but my hands are kinda tied on this one.",
    q: '¿Te va a ayudar?',
    options: [
      { text: 'Sí, pero más tarde', ok: false },
      { text: 'No: está diciendo que no puede, de la forma más amable posible', ok: true },
      { text: 'Necesita que le expliques mejor', ok: false },
    ],
    why: '"My hands are tied" significa que no tiene autoridad para decidirlo. "I hear you" reconoce sin comprometer: es la estructura estándar del rechazo cortés en inglés americano.',
  },
  {
    id: 'er-07', tag: 'detail_audio', level: 'C1', accent: 'en-GB', rate: 1.3,
    line: "We're looking at roughly fifteen hundred quid a month, give or take.",
    q: '¿De cuánto hablan?',
    options: [
      { text: 'Unas 1500 libras mensuales, aproximadamente', ok: true },
      { text: 'Exactamente 1500 dólares al mes', ok: false },
      { text: 'Entre 15 y 100 libras', ok: false },
    ],
    why: '"Quid" es libra esterlina en habla coloquial. "Fifteen hundred" es 1500 (no "one thousand five hundred") y "give or take" marca aproximación.',
  },
  {
    id: 'er-08', tag: 'gist', level: 'C1', accent: 'en-US', rate: 1.35,
    line: "Long story short, we shipped it, it broke, and we rolled it back within the hour.",
    q: '¿Cómo terminó?',
    options: [
      { text: 'Lo publicaron, falló y lo revirtieron rápido', ok: true },
      { text: 'Decidieron no publicarlo', ok: false },
      { text: 'Sigue roto en producción', ok: false },
    ],
    why: 'Tres verbos técnicos seguidos a velocidad real: "ship" (publicar), "break" (fallar) y "roll back" (revertir). Aquí "roll back" sí es deshacer, a diferencia del "revert" indio del reactivo anterior.',
  },
  {
    id: 'er-09', tag: 'inference_audio', level: 'C2', accent: 'en-GB', rate: 1.3,
    line: "With all due respect, I think we might be solving the wrong problem here.",
    q: '¿Qué está pasando?',
    options: [
      { text: 'Está de acuerdo y quiere afinar el detalle', ok: false },
      { text: 'Discrepa de fondo, y "with all due respect" avisa de que viene el golpe', ok: true },
      { text: 'Pide permiso para hablar', ok: false },
    ],
    why: '"With all due respect" es la señal de alarma más fiable del inglés profesional: casi nunca precede algo respetuoso. Y "might be" es un matiz de cortesía, no de duda real.',
  },
  {
    id: 'er-10', tag: 'detail_audio', level: 'C2', accent: 'en-AU', rate: 1.35,
    line: "She'll be right — chuck it on the list and we'll sort it arvo.",
    q: '¿Cuándo lo resuelven?',
    options: [
      { text: 'Por la tarde', ok: true },
      { text: 'Mañana temprano', ok: false },
      { text: 'La próxima semana', ok: false },
    ],
    why: 'Australiano denso: "she\'ll be right" = no hay problema, "chuck it on" = ponlo en, y "arvo" = afternoon. Nada de esto aparece en un curso, y todo aparece en una llamada con Sídney.',
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   CATÁLOGO
   ═══════════════════════════════════════════════════════════════════════════ */

export const DRILLS = {
  tone: {
    id: 'tone', icon: '🔪', name: 'Tone Surgery', level: 'B2 – C1',
    claim: 'La misma idea en tres temperaturas. Solo una cabe en esa sala.',
    trains: 'Registro y diplomacia',
    accent: 'var(--ar-f-tone)',
    bank: TONE, kind: 'scene',
  },
  subtext: {
    id: 'subtext', icon: '🕵️', name: 'Subtexto', level: 'B2 – C2',
    claim: 'Dice A y quiere decir B. ¿Qué te está diciendo realmente?',
    trains: 'Intención e implicatura',
    accent: 'var(--ar-f-subtext)',
    bank: SUBTEXT, kind: 'line',
  },
  lexis: {
    id: 'lexis', icon: '💎', name: 'Precisión léxica', level: 'B2 – C1',
    claim: 'De big problem a pressing issue. La palabra que va con esa otra.',
    trains: 'Colocaciones y matiz',
    accent: 'var(--ar-f-lexis)',
    bank: LEXIS, kind: 'blank',
  },
  ear: {
    id: 'ear', icon: '👂', name: 'Oído nativo', level: 'B2 – C2',
    claim: 'Velocidad real y acentos: británico, americano, australiano, indio.',
    trains: 'Escucha en condiciones reales',
    accent: 'var(--ar-f-ear)',
    bank: EAR, kind: 'audio',
  },
};

/** Baraja sin mutar el original (Fisher-Yates). */
export function shuffle(list) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}
